import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Save, Package, ShoppingBag, Upload, RefreshCw, RotateCcw, AlertTriangle, CheckCircle, Truck } from "lucide-react";
import api, { formatApiError, adminApi } from "../../lib/api";

const CATEGORIAS = ["Entrenamiento Personal", "Plan de Dieta", "Bono de Sesiones", "Suplementación", "Cuota Mensual", "Alquiler Taquilla", "Otro"];
const CAT_COLOR = {
  "Entrenamiento Personal": "text-[#FFCC00] border-[#FFCC00]/30 bg-[#FFCC00]/8",
  "Plan de Dieta": "text-green-400 border-green-500/30 bg-green-500/8",
  "Bono de Sesiones": "text-purple-400 border-purple-500/30 bg-purple-500/8",
  "Suplementación": "text-blue-400 border-blue-500/30 bg-blue-500/8",
  "Cuota Mensual": "text-orange-400 border-orange-500/30 bg-orange-500/8",
  "Alquiler Taquilla": "text-cyan-400 border-cyan-500/30 bg-cyan-500/8",
  "Otro": "text-white/50 border-white/15 bg-white/5",
};

const INTERVALOS = [
  { value: "monthly", label: "Mensual" },
  { value: "quarterly", label: "Trimestral" },
  { value: "annual", label: "Anual" },
];

const STATUS_INFO = {
  active: { label: "Activa", cls: "text-green-400 bg-green-500/10 border-green-500/30" },
  past_due: { label: "Pago pendiente", cls: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  blocked: { label: "Bloqueada", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  cancelled: { label: "Cancelada", cls: "text-white/30 bg-white/5 border-white/10" },
};

const EMPTY = { nombre: "", descripcion: "", categoria: "Entrenamiento Personal", precio: "", precio_original: "", moneda: "eur", stock: "", activo: true, imagen_url: "", es_sesion: false, sesiones_incluidas: 1, es_recurrente: false, intervalo_recurrencia: "monthly", dias_gracia: 5, etiqueta_notas: "" };

export default function ProductosTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgInputRef = React.useRef(null);
  const [deleteId, setDeleteId] = useState(null);
  const [imgFileCreate, setImgFileCreate] = useState(null);   // file staged for new product
  const [imgPreviewCreate, setImgPreviewCreate] = useState(""); // local preview URL
  const [view, setView] = useState("productos"); // "productos" | "pedidos" | "suscripciones"
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null); // {id, notas}
  const [markingDelivered, setMarkingDelivered] = useState(new Set()); // session_ids in flight

  const load = async () => {
    setLoading(true);
    try { const data = await api.get("/admin/productos").then(r => r.data); setProducts(data); }
    catch { } finally { setLoading(false); }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try { const data = await api.get("/admin/pedidos").then(r => r.data); setOrders(data); }
    catch { } finally { setOrdersLoading(false); }
  };

  const loadSubs = async () => {
    setSubsLoading(true);
    try { const data = await adminApi.listSubscriptions(); setSubs(data); }
    catch { } finally { setSubsLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (view === "pedidos") loadOrders(); }, [view]);
  useEffect(() => { if (view === "suscripciones") loadSubs(); }, [view]);

  const openCreate = () => { setImgFileCreate(null); setImgPreviewCreate(""); setModal({ mode: "create", data: { ...EMPTY } }); };
  const openEdit = (p) => setModal({ mode: "edit", data: { ...p, precio: p.precio, precio_original: p.precio_original ?? "", stock: p.stock ?? "", es_sesion: p.es_sesion || false, sesiones_incluidas: p.sesiones_incluidas || 1, es_recurrente: p.es_recurrente || false, intervalo_recurrencia: p.intervalo_recurrencia || "monthly", dias_gracia: p.dias_gracia ?? 5, etiqueta_notas: p.etiqueta_notas || "" } });

  const handleSave = async () => {
    const d = modal.data;
    if (!d.nombre?.trim() || !d.precio) { setErr("Nombre y precio son obligatorios"); return; }
    if (d.es_recurrente && !d.intervalo_recurrencia) { setErr("Selecciona el intervalo de cobro"); return; }
    setSaving(true); setErr("");
    try {
      const payload = {
        nombre: d.nombre.trim(),
        descripcion: d.descripcion || "",
        categoria: d.categoria || "Otro",
        precio: parseFloat(d.precio),
        precio_original: d.precio_original !== "" && d.precio_original !== null && d.precio_original !== undefined ? parseFloat(d.precio_original) : null,
        moneda: d.moneda || "eur",
        stock: d.stock !== "" && d.stock !== null && d.stock !== undefined ? parseInt(d.stock) : null,
        activo: d.activo,
        imagen_url: d.imagen_url || null,
        es_sesion: d.es_sesion || false,
        sesiones_incluidas: d.es_sesion ? (parseInt(d.sesiones_incluidas) || 1) : 1,
        es_recurrente: d.es_recurrente || false,
        intervalo_recurrencia: d.es_recurrente ? d.intervalo_recurrencia : null,
        dias_gracia: d.es_recurrente ? (parseInt(d.dias_gracia) || 5) : 0,
        etiqueta_notas: d.es_recurrente ? (d.etiqueta_notas || null) : null,
      };
      if (modal.mode === "create") {
        const created = await api.post("/admin/productos", payload).then(r => r.data);
        // If user staged an image file, upload it now to the newly created product
        if (imgFileCreate) {
          try {
            const form = new FormData(); form.append("file", imgFileCreate);
            const res = await api.post(`/admin/productos/${created.id}/upload-imagen`, form, { headers: { "Content-Type": "multipart/form-data" } });
            created.imagen_url = res.data.imagen_url;
          } catch { /* image upload failed silently — product still created */ }
        }
        setProducts(prev => [created, ...prev]);
      } else {
        const updated = await api.put(`/admin/productos/${d.id}`, payload).then(r => r.data);
        setProducts(prev => prev.map(p => p.id === d.id ? updated : p));
      }
      setModal(null);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await api.delete(`/admin/productos/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail) || "Error eliminando");
    } finally { setDeleteId(null); }
  };

  const set = (field, value) => setModal(m => ({ ...m, data: { ...m.data, [field]: value } }));

  const totalRevenue = orders.filter(o => o.payment_status === "paid")
    .reduce((s, o) => s + (o.amount || 0), 0);
  const pendientesEntrega = orders.filter(o => o.payment_status === "paid" && o.estado_entrega !== "entregado").length;

  const handleMarcarEntregado = async (sessionId) => {
    setMarkingDelivered(prev => new Set(prev).add(sessionId));
    try {
      await api.patch(`/admin/pedidos/${sessionId}/entregar`);
      setOrders(prev => prev.map(o => o.session_id === sessionId ? { ...o, estado_entrega: "entregado" } : o));
    } catch { alert("Error al actualizar el pedido"); }
    finally { setMarkingDelivered(prev => { const n = new Set(prev); n.delete(sessionId); return n; }); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="productos-tab">
      {/* Header */}
      <div className="flex items-start justify-between mb-7 gap-4">
        <div>
          <h1 className="text-xl font-black tracking-wider">Tienda &amp; Pedidos</h1>
          <p className="text-xs text-white/40 mt-0.5">{products.length} producto{products.length !== 1 ? "s" : ""} · integrado con Stripe</p>
        </div>
        <div className="flex items-center gap-3">
          {view === "productos" && (
            <button onClick={openCreate} data-testid="nuevo-producto-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
              <Plus size={12} /> Nuevo producto
            </button>
          )}
          <div className="flex border border-white/10">
            <button onClick={() => setView("productos")} data-testid="view-productos-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "productos" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <Package size={12} /> Productos
            </button>
            <button onClick={() => setView("suscripciones")} data-testid="view-suscripciones-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "suscripciones" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <RefreshCw size={12} /> Suscripciones
            </button>
            <button onClick={() => setView("pedidos")} data-testid="view-pedidos-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "pedidos" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <ShoppingBag size={12} /> Pedidos
            </button>
          </div>
        </div>
      </div>

      {/* ===== PRODUCTOS ===== */}
      {view === "productos" && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFCC00]" /></div>
          ) : products.length === 0 ? (
            <div className="border border-white/8 border-dashed p-16 text-center">
              <Package size={40} className="mx-auto text-white/10 mb-4" />
              <p className="text-white/30 text-sm mb-1">No hay productos todavía.</p>
              <p className="text-white/20 text-xs">Crea el primer producto y se sincronizará automáticamente con Stripe.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
              {products.map(p => (
                <ProductCard key={p.id} product={p} onEdit={() => openEdit(p)}
                  onDelete={() => window.confirm(`¿Eliminar "${p.nombre}"?`) && handleDelete(p.id)}
                  deleting={deleteId === p.id} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== SUSCRIPCIONES ===== */}
      {view === "suscripciones" && (
        <div data-testid="suscripciones-view">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs text-white/40">{subs.length} suscripción{subs.length !== 1 ? "es" : ""} en total</p>
            <button onClick={loadSubs} className="flex items-center gap-1.5 text-[10px] text-white/40 hover:text-white uppercase tracking-widest font-bold transition-colors">
              <RefreshCw size={11} /> Actualizar
            </button>
          </div>
          {subsLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFCC00]" /></div>
          ) : subs.length === 0 ? (
            <div className="border border-white/8 border-dashed p-16 text-center">
              <RefreshCw size={40} className="mx-auto text-white/10 mb-4" />
              <p className="text-white/30 text-sm">Todavía no hay suscripciones activas.</p>
              <p className="text-white/20 text-xs mt-1">Aparecerán aquí cuando un socio contrate un producto recurrente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {subs.map(s => (
                <SubRow key={s.id} sub={s}
                  editingNotes={editingNotes}
                  setEditingNotes={setEditingNotes}
                  onSaveNotes={async (id, notas) => {
                    await adminApi.updateSubNotes(id, notas);
                    setSubs(prev => prev.map(x => x.id === id ? { ...x, notas_admin: notas } : x));
                    setEditingNotes(null);
                  }}
                  onCancel={async (id) => {
                    if (!window.confirm("¿Cancelar esta suscripción al final del período?")) return;
                    await adminApi.cancelSubscription(id);
                    setSubs(prev => prev.map(x => x.id === id ? { ...x, cancel_at_period_end: true } : x));
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== PEDIDOS ===== */}
      {view === "pedidos" && (
        <>
          {ordersLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFCC00]" /></div>
          ) : (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-7">
                <StatBox label="Total pedidos" value={orders.length} />
                <StatBox label="Pagados" value={orders.filter(o => o.payment_status === "paid").length} accent />
                <StatBox label="Ingresos" value={`${totalRevenue.toFixed(2)} €`} accent />
                <StatBox label="Pendientes entrega" value={pendientesEntrega} warn={pendientesEntrega > 0} />
              </div>
              {orders.length === 0 ? (
                <div className="border border-white/8 p-12 text-center">
                  <ShoppingBag size={32} className="mx-auto text-white/10 mb-3" />
                  <p className="text-white/30 text-sm">Todavía no hay pedidos.</p>
                </div>
              ) : (
                <div className="border border-white/10 divide-y divide-white/5" data-testid="orders-table">
                  <div className="grid grid-cols-[1fr_1fr_auto_auto_auto_auto] gap-4 px-4 py-2.5 bg-white/3">
                    {["Socio", "Producto", "Importe", "Pago", "Entrega", "Fecha"].map(h => (
                      <p key={h} className="text-[9px] font-black tracking-[0.2em] uppercase text-white/35">{h}</p>
                    ))}
                  </div>
                  {orders.map(o => (
                    <OrderRow key={o.session_id} order={o}
                      onMarcarEntregado={() => handleMarcarEntregado(o.session_id)}
                      loading={markingDelivered.has(o.session_id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ===== MODAL ===== */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-[#0d0d0d] border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <p className="text-sm font-black text-white tracking-wide">
                {modal.mode === "create" ? "Nuevo producto" : "Editar producto"}
              </p>
              <button onClick={() => setModal(null)} className="text-white/30 hover:text-white transition-colors"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-5">
              <Fld label="Nombre *">
                <input value={modal.data.nombre} onChange={e => set("nombre", e.target.value)}
                  data-testid="prod-nombre" placeholder="Ej: Sesión de Entrenamiento Personal" className="inp" />
              </Fld>
              <Fld label="Descripción">
                <textarea value={modal.data.descripcion} onChange={e => set("descripcion", e.target.value)}
                  rows={3} data-testid="prod-descripcion"
                  placeholder="Describe el producto, qué incluye, duración..."
                  className="inp resize-none" />
              </Fld>
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Categoría">
                  <select value={modal.data.categoria} onChange={e => set("categoria", e.target.value)}
                    data-testid="prod-categoria" className="inp appearance-none cursor-pointer">
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Fld>
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Precio de venta (€) *">
                  <input type="number" min="0.01" step="0.01" value={modal.data.precio}
                    onChange={e => set("precio", e.target.value)}
                    data-testid="prod-precio" placeholder="0.00" className="inp" />
                </Fld>
                <Fld label="Precio PVP / tachado (€)">
                  <input type="number" min="0.01" step="0.01" value={modal.data.precio_original || ""}
                    onChange={e => set("precio_original", e.target.value)}
                    data-testid="prod-precio-original" placeholder="Sin descuento" className="inp" />
                  <p className="text-[10px] text-white/25 mt-1">Si lo rellenas, la tienda mostrará el precio rebajado.</p>
                </Fld>
              </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Stock (dejar vacío = ilimitado)">
                  <input type="number" min="1" step="1" value={modal.data.stock}
                    onChange={e => set("stock", e.target.value)}
                    data-testid="prod-stock" placeholder="Ilimitado" className="inp" />
                </Fld>
                <Fld label="Estado">
                  <button type="button" onClick={() => set("activo", !modal.data.activo)}
                    data-testid="prod-activo-toggle"
                    className={`w-full py-2.5 text-[11px] font-black tracking-[0.2em] uppercase border transition-colors ${
                      modal.data.activo
                        ? "bg-green-500/10 border-green-500/40 text-green-400 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400"
                        : "bg-red-500/10 border-red-500/30 text-red-400"
                    }`}>
                    {modal.data.activo ? "Activo" : "Inactivo"}
                  </button>
                </Fld>
              </div>
              <Fld label="¿Producto de sesiones? (el cliente reserva citas tras comprar)">
                <button type="button" onClick={() => set("es_sesion", !modal.data.es_sesion)} data-testid="prod-es-sesion"
                  className={`w-full py-2.5 text-[11px] font-black tracking-[0.2em] uppercase border transition-colors ${modal.data.es_sesion ? "bg-[#FFCC00]/10 border-[#FFCC00]/40 text-[#FFCC00]" : "bg-white/3 border-white/10 text-white/40"}`}>
                  {modal.data.es_sesion ? "Sí — el cliente podrá reservar sesiones" : "No — producto estándar"}
                </button>
              </Fld>
              {modal.data.es_sesion && (
                <Fld label="Número de sesiones incluidas">
                  <input type="number" min="1" step="1" value={modal.data.sesiones_incluidas}
                    onChange={e => set("sesiones_incluidas", e.target.value)}
                    data-testid="prod-sesiones" className="inp" />
                  <p className="text-[10px] text-white/25 mt-1">Al pagar, el cliente recibirá estos créditos para hacer reservas.</p>
                </Fld>
              )}

              {/* ── Recurring subscription ── */}
              <div className="border-t border-white/8 pt-5">
                <Fld label="¿Cobro recurrente? (suscripción mensual, trimestral o anual)">
                  <button type="button" onClick={() => set("es_recurrente", !modal.data.es_recurrente)} data-testid="prod-es-recurrente"
                    className={`w-full py-2.5 text-[11px] font-black tracking-[0.2em] uppercase border transition-colors ${modal.data.es_recurrente ? "bg-orange-500/10 border-orange-500/40 text-orange-400" : "bg-white/3 border-white/10 text-white/40"}`}>
                    {modal.data.es_recurrente ? "Sí — suscripción recurrente" : "No — pago único"}
                  </button>
                </Fld>
                {modal.data.es_recurrente && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Fld label="Intervalo de cobro">
                        <select value={modal.data.intervalo_recurrencia} onChange={e => set("intervalo_recurrencia", e.target.value)}
                          data-testid="prod-intervalo" className="inp appearance-none cursor-pointer">
                          {INTERVALOS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                        </select>
                      </Fld>
                      <Fld label="Días de gracia (si falla el pago)">
                        <input type="number" min="0" max="30" step="1" value={modal.data.dias_gracia}
                          onChange={e => set("dias_gracia", e.target.value)}
                          data-testid="prod-dias-gracia" placeholder="5" className="inp" />
                      </Fld>
                    </div>
                    <Fld label="Etiqueta de notas (opcional — para asignaciones específicas)">
                      <input value={modal.data.etiqueta_notas || ""} onChange={e => set("etiqueta_notas", e.target.value)}
                        data-testid="prod-etiqueta-notas" placeholder="Ej: Número de taquilla, Rutina asignada..." className="inp" />
                      <p className="text-[10px] text-white/25 mt-1">Este campo aparece en la ficha de cada suscripción para que puedas anotar datos específicos del socio.</p>
                    </Fld>
                  </div>
                )}
              </div>
              <Fld label="Imagen del producto">
                <div className="space-y-2">
                  {/* Preview: local file (create) or saved URL (edit) */}
                  {(imgPreviewCreate || modal.data.imagen_url) && (
                    <div className="h-32 border border-white/10 overflow-hidden relative">
                      <img
                        src={imgPreviewCreate || (modal.data.imagen_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${modal.data.imagen_url}` : modal.data.imagen_url)}
                        alt="" className="w-full h-full object-cover" />
                      {imgPreviewCreate && modal.mode === "create" && (
                        <div className="absolute bottom-1 right-1 bg-black/70 text-[#FFCC00] text-[9px] font-black px-1.5 py-0.5 uppercase tracking-wider">Pendiente de subir</div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {/* Upload button: available in both create and edit */}
                    <label className={`flex items-center gap-1.5 px-3 py-2 border text-[10px] font-black tracking-wider uppercase cursor-pointer transition-colors shrink-0 ${uploadingImg ? "border-white/10 text-white/30" : "border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10"}`}>
                      {uploadingImg ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                      {uploadingImg ? "Subiendo..." : "Subir foto"}
                      <input ref={imgInputRef} type="file" accept="image/*" className="hidden" disabled={uploadingImg}
                        onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          if (modal.mode === "create") {
                            // Stage the file — will be uploaded after product is created
                            setImgFileCreate(file);
                            setImgPreviewCreate(URL.createObjectURL(file));
                          } else {
                            // Edit mode — upload immediately
                            setUploadingImg(true);
                            try {
                              const form = new FormData(); form.append("file", file);
                              const res = await api.post(`/admin/productos/${modal.data.id}/upload-imagen`, form, { headers: { "Content-Type": "multipart/form-data" } });
                              set("imagen_url", res.data.imagen_url);
                            } catch { alert("Error subiendo la imagen"); }
                            finally { setUploadingImg(false); if (imgInputRef.current) imgInputRef.current.value = ""; }
                          }
                        }} />
                    </label>
                    <input value={modal.data.imagen_url || ""} onChange={e => { set("imagen_url", e.target.value); setImgPreviewCreate(""); setImgFileCreate(null); }}
                      data-testid="prod-imagen" placeholder="https://... (o sube una foto)" className="inp flex-1 text-xs" />
                  </div>
                  {modal.mode === "create" && imgFileCreate && (
                    <p className="text-[10px] text-[#FFCC00]/60">La foto se subirá automáticamente al crear el producto.</p>
                  )}
                </div>
              </Fld>
              {err && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3" data-testid="prod-error">{err}</p>}
              <p className="text-[10px] text-white/25 border border-white/8 px-3 py-2">
                Al guardar, el producto se sincronizará automáticamente con Stripe para que los socios puedan pagar con tarjeta.
              </p>
              <div className="flex gap-3 pt-1">
                <button onClick={handleSave} disabled={saving || !modal.data.nombre?.trim()} data-testid="prod-save-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3.5 hover:bg-white transition-colors disabled:opacity-40">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Guardando en Stripe..." : modal.mode === "create" ? "Crear y sincronizar" : "Guardar cambios"}
                </button>
                <button onClick={() => setModal(null)} className="px-5 border border-white/15 text-white/50 text-[11px] font-bold tracking-wider uppercase hover:text-white transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
          <style>{`.inp { width:100%; background:#111; border:1px solid rgba(255,255,255,.12); color:#fff; padding:10px 14px; font-size:13px; outline:none; transition:border-color .15s; } .inp:focus { border-color:#FFCC00; }`}</style>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product: p, onEdit, onDelete, deleting }) {
  const catColor = CAT_COLOR[p.categoria] || CAT_COLOR["Otro"];
  return (
    <div className={`border ${p.activo ? "border-white/8" : "border-red-500/15 opacity-60"} bg-[#0a0a0a] flex flex-col`} data-testid={`product-card-${p.id}`}>
      {p.imagen_url && (
        <div className="aspect-square overflow-hidden border-b border-white/5">
          <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className={`text-[9px] font-black tracking-[0.18em] uppercase px-2 py-0.5 border ${catColor}`}>
            {p.categoria}
          </span>
          {!p.activo && <span className="text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border border-red-500/30 text-red-400">Inactivo</span>}
        </div>
        <p className="text-sm font-black text-white leading-snug">{p.nombre}</p>
        {p.descripcion && <p className="text-[11px] text-white/40 line-clamp-2">{p.descripcion}</p>}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="font-display text-xl text-[#FFCC00]">{p.precio.toFixed(2)}<span className="text-sm ml-0.5">€</span></span>
          {p.stock !== null && p.stock !== undefined ? (
            <span className={`text-[10px] font-bold uppercase tracking-wider ${p.stock_disponible > 0 ? "text-green-400" : "text-red-400"}`}>
              {p.stock_disponible ?? p.stock} / {p.stock} plazas
            </span>
          ) : (
            <span className="text-[10px] text-white/25 uppercase tracking-wider font-bold">Ilimitado</span>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onEdit} data-testid={`edit-product-${p.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-white/10 text-white/45 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] text-[10px] font-bold tracking-wider uppercase transition-colors">
            <Pencil size={11} /> Editar
          </button>
          <button onClick={onDelete} disabled={deleting} data-testid={`delete-product-${p.id}`}
            className="p-2 border border-white/10 text-white/30 hover:border-red-500/40 hover:text-red-400 transition-colors disabled:opacity-40">
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order: o, onMarcarEntregado, loading }) {
  const statusColor = {
    paid: "text-green-400 border-green-500/30",
    pending: "text-yellow-400 border-yellow-500/30",
    failed: "text-red-400 border-red-500/30",
    expired: "text-white/30 border-white/15",
    initiated: "text-white/40 border-white/15",
  };
  const statusLabel = { paid: "Pagado", pending: "Pendiente", failed: "Fallido", expired: "Caducado", initiated: "Iniciado" };
  const s = o.payment_status || "initiated";
  const entregado = o.estado_entrega === "entregado";

  return (
    <div className="grid grid-cols-[1fr_1fr_auto_auto_auto_auto] gap-4 px-4 py-3 hover:bg-white/[0.02] items-center" data-testid={`order-row-${o.session_id}`}>
      <div className="min-w-0">
        <p className="text-sm text-white font-bold truncate">{o.user_nombre || "—"}</p>
        <p className="text-[10px] text-white/30 truncate">{o.user_email || "—"}</p>
      </div>
      <p className="text-sm text-white/70 truncate">{o.product_nombre || "—"}</p>
      <span className="font-black text-[#FFCC00] text-sm whitespace-nowrap">{(o.amount || 0).toFixed(2)} €</span>
      <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border ${statusColor[s] || "text-white/30 border-white/10"}`}>{statusLabel[s] || s}</span>
      {/* Delivery column */}
      {entregado ? (
        <span className="flex items-center gap-1 text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border text-green-400 border-green-500/30 bg-green-500/8 whitespace-nowrap"
          data-testid={`order-entregado-${o.session_id}`}>
          <CheckCircle size={9} /> Entregado
        </span>
      ) : s === "paid" ? (
        <button onClick={onMarcarEntregado} disabled={loading}
          data-testid={`order-entregar-btn-${o.session_id}`}
          className="flex items-center gap-1 text-[9px] font-black tracking-widest uppercase px-2 py-1 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10 transition-colors disabled:opacity-40 whitespace-nowrap">
          {loading ? <Loader2 size={9} className="animate-spin" /> : <Truck size={9} />}
          Entregar
        </button>
      ) : (
        <span className="text-[9px] text-white/20 uppercase tracking-widest font-bold whitespace-nowrap">—</span>
      )}
      <span className="text-[10px] text-white/25 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleDateString("es-ES") : "—"}</span>
    </div>
  );
}

function StatBox({ label, value, accent, warn }) {
  return (
    <div className={`border ${warn ? "border-orange-500/30 bg-orange-500/5" : accent ? "border-[#FFCC00]/20 bg-[#FFCC00]/3" : "border-white/8"} px-4 py-4`}>
      <p className="text-[9px] uppercase tracking-[0.25em] font-black text-white/35 mb-1">{label}</p>
      <p className={`font-display text-2xl ${warn ? "text-orange-400" : accent ? "text-[#FFCC00]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-1.5">{label}</label>
      {children}
    </div>
  );
}


function SubRow({ sub: s, editingNotes, setEditingNotes, onSaveNotes, onCancel }) {
  const si = STATUS_INFO[s.status] || STATUS_INFO.active;
  const intLabel = { monthly: "Mensual", quarterly: "Trimestral", annual: "Anual" }[s.intervalo] || s.intervalo;
  const periodEnd = s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("es-ES") : "—";
  const isEditing = editingNotes?.id === s.id;
  const [notaVal, setNotaVal] = React.useState(s.notas_admin || "");

  return (
    <div className="border border-white/8 bg-white/[0.02] p-4 space-y-3" data-testid={`sub-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm text-white truncate">{s.product_nombre}</p>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[9px] font-black uppercase tracking-widest ${si.cls}`}>
              {s.status === "past_due" && <AlertTriangle size={9} />}
              {s.status === "active" && <CheckCircle size={9} />}
              {si.label}
            </span>
            {s.cancel_at_period_end && <span className="px-2 py-0.5 border border-orange-500/30 text-orange-400 text-[9px] uppercase tracking-widest font-black">Cancela al fin del período</span>}
          </div>
          <p className="text-xs text-white/40 mt-0.5">{s.user_nombre || s.user_email} · {intLabel} · Renueva: {periodEnd}</p>
          {s.status === "past_due" && s.grace_period_end && (
            <p className="text-[10px] text-orange-400 mt-0.5">Gracia hasta: {new Date(s.grace_period_end).toLocaleDateString("es-ES")}</p>
          )}
        </div>
        {!s.cancel_at_period_end && s.status !== "cancelled" && s.status !== "blocked" && (
          <button onClick={() => onCancel(s.id)} className="shrink-0 px-2 py-1 border border-red-500/30 text-red-400 text-[9px] font-black uppercase hover:bg-red-500/10 transition-colors">
            Cancelar
          </button>
        )}
      </div>

      {/* Notes row */}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <input value={notaVal} onChange={e => setNotaVal(e.target.value)}
              autoFocus placeholder={s.etiqueta_notas || "Notas internas..."}
              className="flex-1 bg-black border border-white/15 text-white text-xs px-2 py-1.5 outline-none focus:border-[#FFCC00]" />
            <button onClick={() => onSaveNotes(s.id, notaVal)} className="px-2 py-1.5 bg-[#FFCC00] text-black text-[10px] font-black uppercase">Guardar</button>
            <button onClick={() => setEditingNotes(null)} className="px-2 py-1.5 border border-white/10 text-white/40 text-[10px] font-black uppercase">×</button>
          </>
        ) : (
          <button onClick={() => { setEditingNotes({ id: s.id }); setNotaVal(s.notas_admin || ""); }}
            className="flex items-center gap-1.5 text-[10px] text-white/35 hover:text-white/70 transition-colors">
            <Pencil size={10} />
            {s.notas_admin ? <span className="text-white/60">{s.notas_admin}</span> : <span>{s.etiqueta_notas || "Añadir notas internas"}</span>}
          </button>
        )}
      </div>
    </div>
  );
}
